import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';

import type {
  CompanyFileDto,
  CompanyFileReferencesDto,
  FileLibraryListResult,
  FileLibraryListParams,
  FileLibraryUsageDto,
  UploadCompanyFileInput,
  UploadCompanyFileResult,
} from './types';

export const normalizeFileLibraryListParams = (
  params: FileLibraryListParams = {},
): Record<string, string | number | boolean> => {
  const { batch_id, company_id, ...filters } = params as FileLibraryListParams & {
    batch_id?: string;
    company_id?: string;
  };
  const scope =
    batch_id && batch_id !== ''
      ? { batch_id }
      : company_id && company_id !== ''
        ? { company_id }
        : {};
  const cleaned = Object.fromEntries(
    Object.entries({
      ...filters,
      ...scope,
    }).filter(([, value]) => value !== undefined && value !== ''),
  ) as Record<string, string | number | boolean>;
  if (cleaned.mime_group === 'all') delete cleaned.mime_group;
  return cleaned;
};

export const fetchFileLibraryUsage = async (companyId?: string): Promise<FileLibraryUsageDto> => {
  const res = await apiClient.get<ApiSuccess<FileLibraryUsageDto>>('/file-library/usage', {
    params: companyId ? { company_id: companyId } : undefined,
  });
  return res.data.data;
};

export const fetchFileLibraryFiles = async (
  params: FileLibraryListParams = {},
): Promise<FileLibraryListResult> => {
  const cleaned = normalizeFileLibraryListParams(params);
  const res = await apiClient.get<ApiSuccess<FileLibraryListResult>>('/file-library', {
    params: cleaned,
  });
  return res.data.data;
};

export const uploadCompanyFile = async ({
  file,
  company_id,
  idempotency_key,
}: UploadCompanyFileInput): Promise<UploadCompanyFileResult> => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('idempotency_key', idempotency_key ?? crypto.randomUUID());
  if (company_id) fd.append('company_id', company_id);
  const res = await apiClient.post<ApiSuccess<UploadCompanyFileResult>>(
    '/file-library/uploads',
    fd,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return res.data.data;
};

export const fetchCompanyFile = async (companyFileId: string): Promise<CompanyFileDto> => {
  const res = await apiClient.get<ApiSuccess<CompanyFileDto>>(`/file-library/${companyFileId}`);
  return res.data.data;
};

export const fetchCompanyFileReferences = async (
  companyFileId: string,
): Promise<CompanyFileReferencesDto> => {
  const res = await apiClient.get<ApiSuccess<CompanyFileReferencesDto>>(
    `/file-library/${companyFileId}/references`,
  );
  return res.data.data;
};

export const archiveCompanyFile = async (companyFileId: string): Promise<CompanyFileDto> => {
  const res = await apiClient.post<ApiSuccess<CompanyFileDto>>(
    `/file-library/${companyFileId}/archive`,
  );
  return res.data.data;
};

export const restoreCompanyFile = async (companyFileId: string): Promise<CompanyFileDto> => {
  const res = await apiClient.post<ApiSuccess<CompanyFileDto>>(
    `/file-library/${companyFileId}/restore`,
  );
  return res.data.data;
};

export const companyFileContentUrl = (companyFileId: string): string => {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  return `${base}/file-library/${companyFileId}/content`;
};

export const companyFileThumbnailUrl = (companyFileId: string): string => {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  return `${base}/file-library/${companyFileId}/thumbnail`;
};

export const companyFilePreviewUrl = (companyFileId: string): string => {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  return `${base}/file-library/${companyFileId}/preview`;
};
